import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, ArrowLeft, GripVertical, Clock, Coffee, Trash2,
  Calendar, Building2, ChevronRight, ChevronDown, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { useScheduleData } from "@/hooks/useScheduleData";
import { tasksApi, blocksApi, formatApiError } from "@/lib/api";
import { fmtDuration, fmtTime, addMinutes, diffMin, isoNoMs } from "@/lib/format";
import { parseDuration } from "@/lib/durationParse";
import { parseTime } from "@/lib/timeParse";
import { roomColor } from "@/components/schedule/FilterBar";
import ProjectSwitcher from "@/components/schedule/ProjectSwitcher";

/**
 * Planning view — split screen, three task states.
 *
 *   ┌────────── ROOMS ──────────┬──────── SCHEDULE ─────────┐
 *   │ UNSCHEDULED tasks per     │ Per-day groups:           │
 *   │ room. Quick-add.          │  • header  → drop = plan  │
 *   │ Multi-select + drag.      │  • PLANNED chips → drag   │
 *   │ Drop zone for unschedule. │  • hourly time slots →    │
 *   │                           │    drop = schedule        │
 *   │                           │  • existing blocks inline │
 *   └───────────────────────────┴───────────────────────────┘
 *
 * Three states (derived):
 *   - unscheduled : no block, no planned_date         (in ROOMS)
 *   - planned     : planned_date set, no block        (in day's PLANNED strip)
 *   - scheduled   : has block                         (in day's timeline)
 *
 * Drag payloads (custom MIME, so each panel's dragover can early-reject):
 *   - application/x-ps-floating-task     : {kind, task_ids[]}
 *   - application/x-ps-planned-task      : {kind, task_ids[]}
 *   - application/x-ps-scheduled-block   : {kind, task_ids[], block_ids[]}
 */

const TIMELINE_START_HOUR = 6;
const TIMELINE_END_HOUR = 22; // inclusive (06–22 = 17 hourly slots)

export default function PlanningView({ onSwitchToSchedule }) {
  const { user } = useAuth();
  const {
    projects, project, data, loading,
    selectProject, refresh,
    scheduleFloatingAtDay, scheduleFloatingAt, planTaskOnDay,
  } = useScheduleData();

  const tasks = useMemo(() => data?.tasks || [], [data?.tasks]);
  const rooms = useMemo(() => data?.rooms || [], [data?.rooms]);
  const blocks = useMemo(() => data?.schedule_blocks || [], [data?.schedule_blocks]);
  const taskById = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t])), [tasks]);
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);

  // Block index by task id (one active block max per task in v1).
  const blockByTaskId = useMemo(() => {
    const m = {};
    for (const b of blocks) {
      if (b.deleted_at) continue;
      m[b.task_id] = b;
    }
    return m;
  }, [blocks]);

  // Derive task state. {unscheduled: Map<roomId|null, Task[]>, plannedByDay: Map<dateKey, Task[]>}
  const { roomGroups, plannedByDay, counts } = useMemo(() => {
    const rg = new Map();
    rg.set(null, []);
    for (const r of rooms) rg.set(r.id, []);
    const pbd = new Map();
    let nUnsched = 0;
    let nPlanned = 0;
    for (const t of tasks) {
      if (t.deleted_at || t.parent_task_id) continue;
      const scheduled = !!blockByTaskId[t.id];
      if (scheduled) continue;
      if (t.planned_date) {
        if (!pbd.has(t.planned_date)) pbd.set(t.planned_date, []);
        pbd.get(t.planned_date).push(t);
        nPlanned += 1;
      } else {
        const key = t.primary_room_id && rg.has(t.primary_room_id) ? t.primary_room_id : null;
        rg.get(key).push(t);
        nUnsched += 1;
      }
    }
    const sortFn = (a, b) => {
      const oa = a.order_within_room ?? 0;
      const ob = b.order_within_room ?? 0;
      if (oa !== ob) return oa - ob;
      return String(a.created_at || "").localeCompare(String(b.created_at || ""));
    };
    for (const arr of rg.values()) arr.sort(sortFn);
    for (const arr of pbd.values()) arr.sort(sortFn);
    return {
      roomGroups: rg,
      plannedByDay: pbd,
      counts: { unscheduled: nUnsched, planned: nPlanned, scheduled: blocks.filter((b) => !b.deleted_at).length },
    };
  }, [rooms, tasks, blocks, blockByTaskId]);

  // Group active blocks by day.
  const blocksByDay = useMemo(() => {
    const m = new Map();
    for (const b of blocks) {
      if (b.deleted_at) continue;
      const d = String(b.start_at || "").slice(0, 10);
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(b);
    }
    for (const arr of m.values()) arr.sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
    return m;
  }, [blocks]);

  // Enumerate every project day so empty days still show as drop targets.
  const dayKeys = useMemo(() => {
    const ks = new Set([...blocksByDay.keys(), ...plannedByDay.keys()]);
    if (project?.start_date && project?.end_date) {
      for (const d of enumerateDays(project.start_date, project.end_date)) ks.add(d);
    }
    return [...ks].sort();
  }, [blocksByDay, plannedByDay, project?.start_date, project?.end_date]);

  // ── selection state (lives at PlanningView so dragged set can cross room sections) ──
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Last clicked id (per panel) used for shift-range select.
  const lastClickedRef = useRef(null);

  // The flat ordered list of unscheduled task IDs across all rooms, in render order.
  // Used to resolve shift-range selection within the ROOMS panel.
  const orderedUnscheduledIds = useMemo(() => {
    const out = [];
    for (const arr of roomGroups.values()) for (const t of arr) out.push(t.id);
    return out;
  }, [roomGroups]);

  const toggleSelection = (taskId, e) => {
    const id = taskId;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (e.shiftKey && lastClickedRef.current) {
        const list = orderedUnscheduledIds;
        const i = list.indexOf(lastClickedRef.current);
        const j = list.indexOf(id);
        if (i >= 0 && j >= 0) {
          const [lo, hi] = i < j ? [i, j] : [j, i];
          for (let k = lo; k <= hi; k++) next.add(list[k]);
          return next;
        }
      }
      if (e.metaKey || e.ctrlKey) {
        if (next.has(id)) next.delete(id); else next.add(id);
        lastClickedRef.current = id;
        return next;
      }
      // Plain click: replace selection with just this id.
      const single = new Set([id]);
      lastClickedRef.current = id;
      return single;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    lastClickedRef.current = null;
  };

  // Clear selection when the underlying unscheduled set changes substantially.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(orderedUnscheduledIds);
      const kept = new Set([...prev].filter((id) => valid.has(id)));
      return kept.size === prev.size ? prev : kept;
    });
  }, [orderedUnscheduledIds]);

  // ── DnD orchestrator ───────────────────────────────────────────
  const unscheduleBlocks = async (blockIds) => {
    try {
      await Promise.all(blockIds.map((id) => blocksApi.remove(id)));
      toast.success(blockIds.length === 1 ? "Unscheduled — task is back in ROOMS" : `${blockIds.length} blocks unscheduled`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unschedule.");
    }
  };

  const unplanTasks = async (taskIds) => {
    try {
      await Promise.all(taskIds.map((id) => tasksApi.update(id, { planned_date: "" })));
      toast.success(taskIds.length === 1 ? "Returned to ROOMS" : `${taskIds.length} tasks returned to ROOMS`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to un-plan.");
    }
  };

  const planTasksOnDay = async (taskIds, dateKey) => {
    try {
      await Promise.all(taskIds.map((id) => planTaskOnDay(id, dateKey)));
      toast.success(taskIds.length === 1 ? `Planned for ${fmtDayLabel(dateKey)}` : `${taskIds.length} planned for ${fmtDayLabel(dateKey)}`);
    } catch {
      // planTaskOnDay handles its own errors
    }
  };

  const scheduleTasksAt = async (taskIds, dateKey, hhmm) => {
    // Sequential, so the project clock-time bands fill in order (08, 09, 10…).
    for (const id of taskIds) {
      // eslint-disable-next-line no-await-in-loop
      await scheduleFloatingAt(id, dateKey, hhmm);
    }
    toast.success(taskIds.length === 1 ? `Scheduled at ${hhmm}` : `${taskIds.length} scheduled from ${hhmm}`);
  };

  /** Apply a partial update to a single schedule block. Used by the inline
   *  length editor on BlockCard (drag-resize and direct time/duration edits). */
  const patchBlock = async (blockId, patch) => {
    try {
      await blocksApi.update(blockId, patch);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to update block");
      throw e;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-bg-base flex items-center justify-center">
        <div className="mono text-2xs uppercase tracking-widest text-ink-mute">LOADING PLAN…</div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-bg-base flex flex-col" data-testid="planning-view">
      <Header
        onBack={onSwitchToSchedule}
        projects={projects}
        currentProjectId={project?.id}
        onSelectProject={selectProject}
        counts={counts}
        userName={user?.name}
      />

      <div className="flex-1 flex min-h-0">
        <RoomsPanel
          rooms={rooms}
          roomGroups={roomGroups}
          project={project}
          selectedIds={selectedIds}
          onTaskClick={toggleSelection}
          onClearSelection={clearSelection}
          onRefresh={refresh}
          onDropUnschedule={({ block_ids = [], task_ids = [], kind }) => {
            if (kind === "scheduled-block" && block_ids.length) unscheduleBlocks(block_ids);
            else if (kind === "planned-task" && task_ids.length) unplanTasks(task_ids);
          }}
        />

        <SchedulePanel
          dayKeys={dayKeys}
          blocksByDay={blocksByDay}
          plannedByDay={plannedByDay}
          taskById={taskById}
          roomById={roomById}
          project={project}
          selectedIds={selectedIds}
          onClearSelection={clearSelection}
          onPlan={planTasksOnDay}
          onScheduleAt={scheduleTasksAt}
          onScheduleAtOnsite={async (taskIds, dateKey) => {
            for (const id of taskIds) {
              // eslint-disable-next-line no-await-in-loop
              await scheduleFloatingAtDay(id, dateKey);
            }
          }}
          onUnscheduleBlocks={unscheduleBlocks}
          onPatchBlock={patchBlock}
        />
      </div>
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────

function Header({ onBack, projects, currentProjectId, onSelectProject, counts, userName }) {
  return (
    <header className="h-12 px-3 border-b border-line-strong flex items-center gap-3 bg-bg-base">
      <button onClick={onBack} className="btn-ghost flex items-center gap-1 px-2" data-testid="back-to-schedule" title="Back to live schedule view">
        <ArrowLeft className="w-3 h-3" /> <span className="hidden md:inline">SCHEDULE</span>
      </button>
      <div className="h-5 w-px bg-line" />
      <div className="mono text-2xs uppercase tracking-[0.2em] text-amber">PLAN</div>
      <div className="h-5 w-px bg-line" />
      <ProjectSwitcher projects={projects || []} currentProjectId={currentProjectId} onSelect={onSelectProject} />
      <div className="flex-1" />
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute hidden md:flex gap-3 tabular-nums">
        <span><span className="text-amber">{counts.unscheduled}</span> UNSCHED</span>
        <span className="opacity-40">·</span>
        <span><span className="text-violet-300">{counts.planned}</span> PLANNED</span>
        <span className="opacity-40">·</span>
        <span><span className="text-cyan">{counts.scheduled}</span> SCHEDULED</span>
      </div>
      {userName && (
        <div className="mono text-2xs tracking-widest text-ink-mute hidden lg:block ml-3">{userName}</div>
      )}
    </header>
  );
}

// ── ROOMS panel (left) ────────────────────────────────────────────

function RoomsPanel({ rooms, roomGroups, project, selectedIds, onTaskClick, onClearSelection, onRefresh, onDropUnschedule }) {
  const [dragOver, setDragOver] = useState(false);

  const onDragOver = (e) => {
    const types = Array.from(e.dataTransfer.types);
    if (types.includes("application/x-ps-scheduled-block") || types.includes("application/x-ps-planned-task")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOver(true);
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const raw = e.dataTransfer.getData("application/x-ps-scheduled-block")
      || e.dataTransfer.getData("application/x-ps-planned-task");
    if (!raw) return;
    try { onDropUnschedule(JSON.parse(raw)); }
    catch (err) { console.warn("Unschedule payload malformed", err); }
  };

  return (
    <aside
      className={
        "w-[420px] shrink-0 border-r border-line-strong bg-bg-elevated flex flex-col min-h-0 " +
        (dragOver ? "outline outline-2 outline-amber outline-offset-[-2px] bg-amber-glow/20" : "")
      }
      onDragOver={onDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid="planning-rooms-panel"
    >
      <div className="px-3 py-2 border-b border-line-strong sticky top-0 bg-bg-elevated z-10 flex items-center gap-2">
        <Building2 className="w-3 h-3 text-amber" />
        <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">ROOMS</span>
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute">· unscheduled only</span>
        {selectedIds.size > 0 && (
          <span className="ml-auto mono text-2xs uppercase tracking-widest text-amber tabular-nums" data-testid="rooms-selection-count">
            {selectedIds.size} SEL
            <button onClick={onClearSelection} className="ml-2 text-ink-mute hover:text-ink" title="Clear selection">×</button>
          </span>
        )}
        {selectedIds.size === 0 && (
          <span className="mono text-2xs uppercase tracking-widest text-ink-mute ml-auto">
            drop here ↩ to unschedule
          </span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {Array.from(roomGroups.entries()).map(([roomId, items]) => {
          const room = roomId ? rooms.find((r) => r.id === roomId) : null;
          return (
            <RoomGroup
              key={roomId || "unassigned"}
              room={room}
              tasks={items}
              project={project}
              selectedIds={selectedIds}
              onTaskClick={onTaskClick}
              onAdded={onRefresh}
            />
          );
        })}
      </div>
    </aside>
  );
}

function RoomGroup({ room, tasks, project, selectedIds, onTaskClick, onAdded }) {
  const [open, setOpen] = useState(true);
  const [title, setTitle] = useState("");
  const [dur, setDur] = useState("60");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const colorBar = room ? roomColor(room.color_token) : "#666";

  const addQuick = async () => {
    if (!project || !title.trim()) {
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const minutes = parseDuration(dur) || 60;
      const nextOrder = (tasks.reduce((m, t) => Math.max(m, t.order_within_room ?? 0), 0)) + 10;
      await tasksApi.create(project.id, {
        title: title.trim(), duration_min: minutes,
        primary_room_id: room?.id || null,
        is_floating: true, order_within_room: nextOrder,
        status: "planned", priority: "normal",
      });
      setTitle("");
      onAdded && onAdded();
      requestAnimationFrame(() => inputRef.current?.focus());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add task");
    } finally {
      setBusy(false);
    }
  };

  const removeTask = async (taskId) => {
    try {
      await tasksApi.remove(taskId);
      onAdded && onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <section className="border-b border-line" data-testid={`plan-room-${room?.id || "unassigned"}`}>
      <header className="flex items-center gap-2 px-3 py-2 bg-bg-elevated cursor-pointer hover:bg-amber-glow/10 transition-colors" onClick={() => setOpen((o) => !o)}>
        <span className="shrink-0">
          {open ? <ChevronDown className="w-3 h-3 text-ink-mute" /> : <ChevronRight className="w-3 h-3 text-ink-mute" />}
        </span>
        <span className="w-1.5 h-5 shrink-0" style={{ background: colorBar }} />
        <span className="mono text-2xs uppercase tracking-widest text-ink truncate">
          {room ? `${room.abbreviation} · ${room.name}` : "UNASSIGNED"}
        </span>
        <span className="mono text-2xs text-ink-mute ml-auto">{tasks.length}</span>
      </header>
      {open && (
        <>
          <div className="px-3 py-1.5 flex items-center gap-1.5 border-b border-line-subtle">
            <input
              ref={inputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addQuick(); } }}
              placeholder="Quick-add task…"
              disabled={busy}
              className="flex-1 bg-bg-base border border-line-strong text-ink mono text-xs px-2 py-1"
              data-testid={`plan-input-${room?.id || "unassigned"}`}
              spellCheck={false}
              autoComplete="off"
            />
            <input
              value={dur}
              onChange={(e) => setDur(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addQuick()}
              placeholder="60"
              title="Duration — 60, 1h 30m"
              className="bg-bg-base border border-line-strong text-ink mono text-xs px-2 py-1 w-14 tabular-nums"
              data-testid={`plan-dur-${room?.id || "unassigned"}`}
              spellCheck={false}
              autoComplete="off"
            />
            <button onClick={addQuick} disabled={busy} className="btn-amber px-2 py-1 mono text-2xs uppercase tracking-widest" data-testid={`plan-save-${room?.id || "unassigned"}`}>
              <Plus className="w-3 h-3" />
            </button>
          </div>
          {tasks.length === 0 ? (
            <div className="px-3 py-2 text-2xs text-ink-mute italic">No unscheduled tasks.</div>
          ) : (
            <ul className="p-2 space-y-1">
              {tasks.map((t) => (
                <FloatingCard
                  key={t.id}
                  task={t}
                  selected={selectedIds.has(t.id)}
                  selectedIds={selectedIds}
                  onClick={(e) => onTaskClick(t.id, e)}
                  onDelete={() => removeTask(t.id)}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function FloatingCard({ task, selected, selectedIds, onClick, onDelete }) {
  const onDragStart = (e) => {
    // Drag the selection set if this card is selected, else just this card.
    const ids = selected && selectedIds.size > 1
      ? Array.from(selectedIds)
      : [task.id];
    const payload = JSON.stringify({ kind: "floating-task", task_ids: ids });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-ps-floating-task", payload);
    e.dataTransfer.setData("text/plain", payload);

    // Custom drag image: badge with count when dragging multiple.
    if (ids.length > 1) {
      const ghost = document.createElement("div");
      ghost.style.cssText = "position:absolute;top:-9999px;left:-9999px;padding:6px 10px;background:#1a1410;color:#fbbf24;border:1px solid #fbbf24;font-family:ui-monospace,monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;border-radius:2px;";
      ghost.textContent = `× ${ids.length} TASKS`;
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 12, 12);
      requestAnimationFrame(() => ghost.remove());
    }
  };

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className={
        "border p-2 cursor-grab active:cursor-grabbing group transition-colors " +
        (selected ? "border-amber bg-amber-glow/15 ring-1 ring-amber" : "bg-bg-base border-line hover:border-amber")
      }
      data-testid={`plan-card-${task.id}`}
      title="Click to select · Shift/⌘+click to multi-select · Drag onto a day"
    >
      <div className="flex items-center gap-2">
        <GripVertical className="w-3 h-3 text-ink-mute shrink-0" />
        <span className="text-sm text-ink truncate flex-1" title={task.title}>
          {task.kind === "meal" && <Coffee className="w-3 h-3 inline mr-1 text-amber" />}
          {task.title}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="opacity-0 group-hover:opacity-100 text-red hover:text-red-light p-0.5 transition-opacity"
          data-testid={`plan-card-delete-${task.id}`}
          title="Delete"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      <div className="flex items-center gap-2 mt-1 text-2xs text-ink-mute mono uppercase tracking-widest">
        <Clock className="w-2.5 h-2.5" />
        <span className="tabular-nums">{fmtDuration(task.duration_min || 60)}</span>
        {task.priority && task.priority !== "normal" && (
          <span className={task.priority === "critical" ? "text-red" : task.priority === "high" ? "text-amber" : "text-ink-mute"}>
            {task.priority}
          </span>
        )}
      </div>
    </li>
  );
}

// ── SCHEDULE panel (right) ────────────────────────────────────────

function SchedulePanel({
  dayKeys, blocksByDay, plannedByDay, taskById, roomById, project, selectedIds, onClearSelection,
  onPlan, onScheduleAt, onScheduleAtOnsite, onUnscheduleBlocks, onPatchBlock,
}) {
  // Expanded-parent state lives at the panel level so it survives day-group
  // collapses and persists across renders triggered by mutations. Set of
  // parent task IDs that are currently expanded. New parents default to
  // expanded — see resolveExpanded below.
  const [collapsedParents, setCollapsedParents] = useState(() => new Set());
  const toggleParent = (parentId) => {
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId); else next.add(parentId);
      return next;
    });
  };
  const isExpanded = (parentId) => !collapsedParents.has(parentId);
  return (
    <section className="flex-1 flex flex-col min-w-0 bg-bg-base" data-testid="planning-schedule-panel">
      <div className="px-3 py-2 border-b border-line-strong bg-bg-elevated flex items-center gap-2 sticky top-0 z-10">
        <Calendar className="w-3 h-3 text-amber" />
        <span className="mono text-2xs uppercase tracking-[0.2em] text-amber">SCHEDULE</span>
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute">· drop header = plan · drop slot = schedule</span>
        <span className="ml-auto mono text-2xs uppercase tracking-widest text-ink-mute">
          {selectedIds.size > 0 ? <button onClick={onClearSelection} className="text-amber hover:text-ink">CLEAR {selectedIds.size}</button> : null}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {dayKeys.length === 0 ? (
          <div className="p-12 text-center text-2xs text-ink-mute italic">Set project start/end dates first.</div>
        ) : (
          dayKeys.map((dateKey) => (
            <DayGroup
              key={dateKey}
              dateKey={dateKey}
              blocks={blocksByDay.get(dateKey) || []}
              plannedTasks={plannedByDay.get(dateKey) || []}
              taskById={taskById}
              roomById={roomById}
              project={project}
              onPlan={onPlan}
              onScheduleAt={onScheduleAt}
              onScheduleAtOnsite={onScheduleAtOnsite}
              onUnscheduleBlock={(id) => onUnscheduleBlocks([id])}
              onPatchBlock={onPatchBlock}
              isExpanded={isExpanded}
              onToggleParent={toggleParent}
            />
          ))
        )}
      </div>
    </section>
  );
}

function DayGroup({ dateKey, blocks, plannedTasks, taskById, roomById, project, onPlan, onScheduleAt, onScheduleAtOnsite, onUnscheduleBlock, onPatchBlock, isExpanded, onToggleParent }) {
  const [headerDragOver, setHeaderDragOver] = useState(false);
  const [open, setOpen] = useState(true);
  const onsite = project?.settings?.days?.[dateKey]?.onsite_time;

  // Drop on header = plan only (no time).
  const headerDragOverHandler = (e) => {
    if (Array.from(e.dataTransfer.types).includes("application/x-ps-floating-task")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setHeaderDragOver(true);
    }
  };
  const headerDropHandler = (e) => {
    e.preventDefault();
    setHeaderDragOver(false);
    const raw = e.dataTransfer.getData("application/x-ps-floating-task");
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (payload.kind === "floating-task" && payload.task_ids?.length) {
        onPlan(payload.task_ids, dateKey);
      }
    } catch (err) { console.warn(err); }
  };

  // Compute hour slots and intersperse existing blocks.
  // Subtask grouping: if a block's task has a parent task (with no schedule
  // block of its own — the standard model per Option B), we hide that block
  // from its natural hour slot and surface it nested under a synthesized
  // "parent group" header at the earliest child's slot. Mirrors the schedule
  // grid's nesting behavior so subtasks appear under their parent everywhere.
  const slots = useMemo(() => {
    const childBlocksByParent = new Map(); // parent_task_id -> [block]
    const flatBlocks = [];
    for (const b of blocks) {
      const t = taskById[b.task_id];
      const parentId = t?.parent_task_id;
      const parentExists = parentId && taskById[parentId];
      if (parentExists) {
        const list = childBlocksByParent.get(parentId) || [];
        list.push(b);
        childBlocksByParent.set(parentId, list);
      } else {
        flatBlocks.push(b);
      }
    }
    // Sort children within each group by start time.
    for (const arr of childBlocksByParent.values()) {
      arr.sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)));
    }
    // Each parent group anchors at its earliest child's hour slot.
    const parentGroups = [];
    for (const [parentId, kids] of childBlocksByParent) {
      const earliest = kids[0].start_at;
      parentGroups.push({
        kind: "parent_group",
        parentId,
        parentTask: taskById[parentId],
        children: kids,
        earliest_start: earliest,
        latest_end: kids[kids.length - 1].end_at,
        hhmm: String(earliest).slice(11, 13) + ":00",
      });
    }
    const byHour = new Map();
    for (const b of flatBlocks) {
      const k = String(b.start_at).slice(11, 13) + ":00";
      if (!byHour.has(k)) byHour.set(k, []);
      byHour.get(k).push({ kind: "block", block: b });
    }
    for (const g of parentGroups) {
      if (!byHour.has(g.hhmm)) byHour.set(g.hhmm, []);
      byHour.get(g.hhmm).push(g);
    }
    // Sort within each slot so parent groups and blocks interleave by start time.
    for (const arr of byHour.values()) {
      arr.sort((a, b) => {
        const aStart = a.kind === "parent_group" ? a.earliest_start : a.block.start_at;
        const bStart = b.kind === "parent_group" ? b.earliest_start : b.block.start_at;
        return String(aStart).localeCompare(String(bStart));
      });
    }
    const out = [];
    for (let h = TIMELINE_START_HOUR; h <= TIMELINE_END_HOUR; h++) {
      const k = `${String(h).padStart(2, "0")}:00`;
      out.push({ hhmm: k, items: byHour.get(k) || [] });
    }
    return out;
  }, [blocks, taskById]);

  return (
    <section className="border-b border-line" data-testid={`plan-day-group-${dateKey}`}>
      <header
        className={
          "flex items-center gap-3 px-3 py-2 bg-bg-elevated/60 cursor-pointer transition-colors " +
          (headerDragOver ? "outline outline-2 outline-violet-300 outline-offset-[-2px] bg-violet-300/10" : "")
        }
        onClick={() => setOpen((o) => !o)}
        onDragOver={headerDragOverHandler}
        onDragLeave={() => setHeaderDragOver(false)}
        onDrop={headerDropHandler}
        data-testid={`plan-day-header-${dateKey}`}
      >
        <span className="shrink-0">
          {open ? <ChevronDown className="w-3 h-3 text-ink-mute" /> : <ChevronRight className="w-3 h-3 text-ink-mute" />}
        </span>
        <span className="mono text-2xs uppercase tracking-[0.2em] text-amber tabular-nums">{fmtDayLabel(dateKey)}</span>
        <span className="mono text-2xs text-ink-mute">
          {plannedTasks.length > 0 && <span className="text-violet-300 mr-2">{plannedTasks.length} planned</span>}
          {blocks.length} scheduled
        </span>
        {onsite && (
          <span className="mono text-2xs text-ink-mute">onsite {onsite}</span>
        )}
        {headerDragOver && (
          <span className="ml-auto mono text-2xs uppercase tracking-widest text-violet-300 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> drop = plan (no time)
          </span>
        )}
      </header>

      {open && (
        <>
          {plannedTasks.length > 0 && (
            <PlannedStrip
              tasks={plannedTasks}
              dateKey={dateKey}
              onSchedule={(taskId) => onScheduleAtOnsite([taskId], dateKey)}
            />
          )}

          <div className="px-2 py-1">
            {slots.map((slot) => (
              <TimeSlot
                key={slot.hhmm}
                dateKey={dateKey}
                hhmm={slot.hhmm}
                items={slot.items}
                taskById={taskById}
                roomById={roomById}
                onDropFloating={(taskIds) => onScheduleAt(taskIds, dateKey, slot.hhmm)}
                onDropPlanned={(taskIds) => onScheduleAt(taskIds, dateKey, slot.hhmm)}
                onUnscheduleBlock={onUnscheduleBlock}
                onPatchBlock={onPatchBlock}
                isExpanded={isExpanded}
                onToggleParent={onToggleParent}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function PlannedStrip({ tasks, dateKey, onSchedule }) {
  return (
    <div className="px-3 py-1.5 border-y border-line-subtle bg-violet-300/[0.04]">
      <div className="mono text-2xs uppercase tracking-widest text-violet-300 mb-1">
        PLANNED · NO TIME ({tasks.length})
      </div>
      <ul className="flex flex-wrap gap-1.5">
        {tasks.map((t) => (
          <PlannedChip key={t.id} task={t} onAutoPlace={() => onSchedule(t.id)} />
        ))}
      </ul>
      <div className="mono text-2xs text-ink-mute mt-1 italic">
        Drag a chip onto a time slot below to schedule · click for onsite-time
      </div>
    </div>
  );
}

function PlannedChip({ task, onAutoPlace }) {
  const onDragStart = (e) => {
    const payload = JSON.stringify({ kind: "planned-task", task_ids: [task.id] });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-ps-planned-task", payload);
    e.dataTransfer.setData("text/plain", payload);
  };
  return (
    <li
      draggable
      onDragStart={onDragStart}
      onClick={onAutoPlace}
      className="bg-bg-elevated border border-violet-300/40 hover:border-violet-300 px-2 py-1 cursor-grab active:cursor-grabbing group transition-colors flex items-center gap-1.5"
      data-testid={`plan-planned-chip-${task.id}`}
      title="Drag onto a time slot · click to place at onsite time · drag to ROOMS to un-plan"
    >
      {task.kind === "meal" && <Coffee className="w-3 h-3 text-amber" />}
      <span className="text-xs text-ink truncate max-w-[12rem]" title={task.title}>{task.title}</span>
      <span className="mono text-2xs text-ink-mute tabular-nums">{fmtDuration(task.duration_min || 60)}</span>
    </li>
  );
}

function TimeSlot({ dateKey, hhmm, items, taskById, roomById, onDropFloating, onDropPlanned, onUnscheduleBlock, onPatchBlock, isExpanded, onToggleParent }) {
  const [dragOver, setDragOver] = useState(false);

  const onDragOver = (e) => {
    const types = Array.from(e.dataTransfer.types);
    if (types.includes("application/x-ps-floating-task") || types.includes("application/x-ps-planned-task")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOver(true);
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const floating = e.dataTransfer.getData("application/x-ps-floating-task");
    const planned = e.dataTransfer.getData("application/x-ps-planned-task");
    try {
      if (floating) {
        const p = JSON.parse(floating);
        if (p.task_ids?.length) onDropFloating(p.task_ids);
      } else if (planned) {
        const p = JSON.parse(planned);
        if (p.task_ids?.length) onDropPlanned(p.task_ids);
      }
    } catch (err) { console.warn(err); }
  };

  return (
    <div
      className={
        "flex items-stretch border-l-2 transition-colors min-h-[28px] " +
        (dragOver ? "border-amber bg-amber-glow/15" : "border-transparent hover:border-line")
      }
      onDragOver={onDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      data-testid={`plan-slot-${dateKey}-${hhmm}`}
    >
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute tabular-nums w-12 shrink-0 py-1.5 pl-1">
        {hhmm}
      </div>
      <div className="flex-1 py-1 pr-1 flex flex-col gap-1">
        {items.length === 0 && (
          <div className={"mono text-2xs italic " + (dragOver ? "text-amber" : "text-ink-mute/40")}>
            {dragOver ? `drop = schedule at ${hhmm}` : "—"}
          </div>
        )}
        {items.map((item) => {
          if (item.kind === "block") {
            return (
              <BlockCard
                key={item.block.id}
                block={item.block}
                task={taskById[item.block.task_id]}
                room={roomById[item.block.room_id]}
                onUnschedule={() => onUnscheduleBlock(item.block.id)}
                onPatchBlock={onPatchBlock}
              />
            );
          }
          // parent_group: synthesized header for a parent task with no schedule
          // block of its own. Expanded by default; tap chevron to collapse.
          return (
            <ParentGroupCard
              key={`pg-${item.parentId}`}
              group={item}
              taskById={taskById}
              roomById={roomById}
              expanded={isExpanded(item.parentId)}
              onToggle={() => onToggleParent(item.parentId)}
              onUnscheduleBlock={onUnscheduleBlock}
              onPatchBlock={onPatchBlock}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Synthesized parent header for a task whose subtasks have their own schedule
 * blocks (the standard Option-B model — parents themselves carry no block).
 * Shows the time window derived from min(child.start) → max(child.end), the
 * parent title, a child-count badge, and a chevron. When expanded, child
 * blocks render indented immediately below — mirroring the schedule grid's
 * parent → subtask nesting.
 */
function ParentGroupCard({ group, taskById, roomById, expanded, onToggle, onUnscheduleBlock, onPatchBlock }) {
  const { parentTask, children, earliest_start, latest_end } = group;
  if (!parentTask) return null;
  const dur = diffMin(earliest_start, latest_end);
  return (
    <div className="flex flex-col" data-testid={`plan-parent-group-${group.parentId}`}>
      <button
        type="button"
        onClick={onToggle}
        className="text-left bg-bg-elevated/70 border border-line hover:border-amber p-1.5 flex items-center gap-2 group transition-colors"
        data-testid={`plan-parent-toggle-${group.parentId}`}
        title={expanded ? "Collapse subtasks" : "Expand subtasks"}
      >
        <ChevronDown
          className={"w-3 h-3 text-ink-mute shrink-0 transition-transform " + (expanded ? "" : "-rotate-90")}
        />
        <span className="mono text-2xs uppercase tracking-widest text-amber tabular-nums shrink-0">
          {fmtTime(earliest_start)}–{fmtTime(latest_end)} · {fmtDuration(dur)}
        </span>
        <span className="text-sm text-ink truncate flex-1" title={parentTask.title}>
          {parentTask.title}
        </span>
        <span
          className="mono text-2xs uppercase tracking-widest border border-amber/50 text-amber px-1 shrink-0"
          title={`${children.length} subtask${children.length === 1 ? "" : "s"}`}
        >
          {children.length} ▸
        </span>
      </button>
      {expanded && (
        <ul className="pl-4 mt-1 flex flex-col gap-1 border-l border-line ml-1" data-testid={`plan-parent-children-${group.parentId}`}>
          {children.map((b) => (
            <li key={b.id} className="flex items-start gap-1.5">
              <span className="mono text-2xs text-ink-mute pt-1.5 shrink-0">↳</span>
              <div className="flex-1">
                <BlockCard
                  block={b}
                  task={taskById[b.task_id]}
                  room={roomById[b.room_id]}
                  onUnschedule={() => onUnscheduleBlock(b.id)}
                  onPatchBlock={onPatchBlock}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BlockCard({ block, task, room, onUnschedule, onPatchBlock }) {
  const [editing, setEditing] = useState(false);
  // Drag-resize state: deltaMin is the live preview shift applied to end_at.
  // Quantized to 15-minute steps via the mouse handler. Persists to backend
  // on mouseup. ~16px = 15min (gives a comfortable feel against the ~32px
  // hourly slot rows).
  const [resizing, setResizing] = useState(null); // { startY, baseEnd, deltaMin }
  const resizeDeltaRef = useRef(0); // sync ref so mouseup can read latest
  const PIX_PER_15MIN = 16;
  const cardRef = useRef(null);
  if (!task) return null;

  const color = roomColor(room?.color_token) || "#666";
  const baseDuration = diffMin(block.start_at, block.end_at);
  const livePreviewEnd = resizing ? isoNoMs(addMinutes(resizing.baseEnd, resizing.deltaMin)) : block.end_at;
  const liveDuration = resizing ? Math.max(15, baseDuration + resizing.deltaMin) : baseDuration;

  const onDragStart = (e) => {
    // Suppress the row's draggable behavior while length-editing.
    if (editing || resizing) {
      e.preventDefault();
      return;
    }
    const payload = JSON.stringify({ kind: "scheduled-block", task_ids: [block.task_id], block_ids: [block.id] });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-ps-scheduled-block", payload);
    e.dataTransfer.setData("text/plain", payload);
  };

  // ── Drag-to-resize handle (bottom edge) ─────────────────────────
  const beginResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onPatchBlock) return;
    const startY = e.clientY;
    const baseEnd = block.end_at;
    resizeDeltaRef.current = 0;
    setResizing({ startY, baseEnd, deltaMin: 0 });

    const onMove = (mv) => {
      const raw = mv.clientY - startY;
      // Quantize to 15-min steps; clamp to 15 min minimum total duration.
      const steps = Math.round(raw / PIX_PER_15MIN);
      const proposed = Math.max(-baseDuration + 15, steps * 15);
      resizeDeltaRef.current = proposed;
      setResizing((s) => (s ? { ...s, deltaMin: proposed } : s));
    };
    const onUp = async () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const finalDelta = resizeDeltaRef.current;
      setResizing(null);
      if (finalDelta === 0) return;
      const newEnd = isoNoMs(addMinutes(baseEnd, finalDelta));
      try {
        await onPatchBlock(block.id, { end_at: newEnd });
        const sign = finalDelta >= 0 ? "+" : "−";
        toast.success(`Duration ${sign}${Math.abs(finalDelta)}m`);
      } catch {
        // onPatchBlock already toasts on failure.
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <>
      <div
        ref={cardRef}
        draggable={!editing && !resizing}
        onDragStart={onDragStart}
        className={
          "bg-bg-elevated border p-1.5 group transition-colors flex items-center gap-2 relative " +
          (resizing ? "border-amber" : "border-line hover:border-amber") +
          (editing || resizing ? "" : " cursor-grab active:cursor-grabbing")
        }
        data-testid={`plan-block-${block.id}`}
        title={editing || resizing ? undefined : "Drag onto rooms panel to unschedule · click time to edit · drag bottom edge to resize"}
      >
        <span className="w-1 self-stretch shrink-0" style={{ background: color }} />
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={
            "mono text-2xs uppercase tracking-widest tabular-nums shrink-0 px-1 -mx-1 py-0.5 rounded-sm hover:bg-amber-glow hover:text-amber transition-colors " +
            (resizing ? "text-amber" : "text-ink-mute")
          }
          title="Click to edit start time + duration"
          data-testid={`plan-block-time-${block.id}`}
        >
          {fmtTime(block.start_at)}–{fmtTime(livePreviewEnd)} <span className="opacity-60">·</span> {fmtDuration(liveDuration)}
        </button>
        <span className="text-sm text-ink truncate flex-1" title={task.title}>{task.title}</span>
        <span className="mono text-2xs uppercase tracking-widest text-ink-mute shrink-0">{room?.abbreviation || "—"}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onUnschedule(); }}
          className="opacity-0 group-hover:opacity-100 mono text-2xs uppercase tracking-widest text-amber hover:bg-amber-glow px-1 py-0.5 transition-opacity"
          data-testid={`plan-block-unschedule-${block.id}`}
          title="Unschedule (drag to rooms panel too)"
        >
          ↩ UNSCHED
        </button>
        {/* Drag-resize handle (bottom edge). Wider than 4px so it's easy to
            grab; visually subtle until hovered. */}
        {onPatchBlock && (
          <div
            onMouseDown={beginResize}
            className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize hover:bg-amber/50 transition-colors"
            title="Drag down/up to resize"
            data-testid={`plan-block-resize-${block.id}`}
          >
            <div className="absolute inset-x-0 bottom-0 h-px bg-line group-hover:bg-amber/60" />
          </div>
        )}
        {resizing && resizing.deltaMin !== 0 && (
          <div className="absolute -right-2 -top-3 z-20 px-1.5 py-0.5 mono text-2xs uppercase tracking-widest bg-amber text-bg-base shadow-md pointer-events-none">
            {resizing.deltaMin > 0 ? "+" : "−"}{Math.abs(resizing.deltaMin)}m
          </div>
        )}
      </div>
      {editing && (
        <BlockLengthEditor
          block={block}
          onClose={() => setEditing(false)}
          onPatchBlock={onPatchBlock}
        />
      )}
    </>
  );
}

/**
 * Modal-ish inline editor for a block's start time + duration. Lives directly
 * below the BlockCard so the user keeps the visual context.
 *
 * Inputs accept the same loose formats as the schedule grid:
 *   - Start: "8a", "14:30", "1730", etc. (parseTime)
 *   - Duration: "2h", "1h30m", "90m", "1.5h", etc. (parseDuration)
 */
function BlockLengthEditor({ block, onClose, onPatchBlock }) {
  const [startDraft, setStartDraft] = useState(fmtTime(block.start_at));
  const [durDraft, setDurDraft] = useState(String(diffMin(block.start_at, block.end_at)));
  const [busy, setBusy] = useState(false);

  const commit = async () => {
    if (busy) return;
    const startHHMM = parseTime(startDraft);
    if (!startHHMM) {
      toast.warning("Invalid start time");
      return;
    }
    const minutes = parseDuration(durDraft);
    if (!minutes || minutes < 15) {
      toast.warning("Duration must be at least 15 minutes");
      return;
    }
    // Build wall-clock ISO using the existing date portion of start_at.
    // See /lib/format.js — slice positions 0..10 keep the date stable.
    const dateKey = String(block.start_at).slice(0, 10);
    const newStart = `${dateKey}T${startHHMM}:00Z`;
    const newEnd = isoNoMs(addMinutes(newStart, minutes));
    setBusy(true);
    try {
      await onPatchBlock(block.id, { start_at: newStart, end_at: newEnd });
      toast.success(`Updated to ${startHHMM} · ${fmtDuration(minutes)}`);
      onClose();
    } catch {
      // onPatchBlock toasts on failure
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="ml-3 bg-bg-elevated border border-amber/60 shadow-[0_4px_16px_rgba(0,0,0,0.4)] px-2 py-1.5 flex items-center gap-2"
      data-testid={`plan-block-length-editor-${block.id}`}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "Enter") commit();
      }}
    >
      <span className="mono text-2xs uppercase tracking-widest text-amber shrink-0">EDIT LENGTH</span>
      <label className="flex items-center gap-1 mono text-2xs uppercase tracking-widest text-ink-mute">
        START
        <input
          autoFocus
          value={startDraft}
          onChange={(e) => setStartDraft(e.target.value)}
          placeholder="HH:MM"
          className="mono text-2xs tabular-nums bg-bg-base border border-line px-1.5 py-0.5 w-16 text-ink focus:outline-none focus:border-amber"
          data-testid={`plan-block-length-editor-start-${block.id}`}
        />
      </label>
      <label className="flex items-center gap-1 mono text-2xs uppercase tracking-widest text-ink-mute">
        DUR
        <input
          value={durDraft}
          onChange={(e) => setDurDraft(e.target.value)}
          placeholder="2h or 90m"
          className="mono text-2xs tabular-nums bg-bg-base border border-line px-1.5 py-0.5 w-20 text-ink focus:outline-none focus:border-amber"
          data-testid={`plan-block-length-editor-dur-${block.id}`}
        />
      </label>
      <button
        type="button"
        onClick={commit}
        disabled={busy}
        className="mono text-2xs uppercase tracking-widest text-bg-base bg-amber px-2 py-0.5 hover:opacity-90 disabled:opacity-50"
        data-testid={`plan-block-length-editor-save-${block.id}`}
      >
        SAVE
      </button>
      <button
        type="button"
        onClick={onClose}
        className="mono text-2xs uppercase tracking-widest text-ink-mute hover:text-ink px-1"
        data-testid={`plan-block-length-editor-cancel-${block.id}`}
      >
        ESC
      </button>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function enumerateDays(start, end) {
  const out = [];
  const s = parseISODate(start);
  const e = parseISODate(end);
  if (!s || !e || s > e) return out;
  const cur = new Date(s);
  while (cur <= e) {
    out.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}
function parseISODate(s) {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y) return null;
  return new Date(y, m - 1, d);
}
function pad(n) { return String(n).padStart(2, "0"); }
function fmtDayLabel(key) {
  const d = parseISODate(key);
  if (!d) return key;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}
